/**
 * Select the orientation being edited. All orientations of a unit share
 * the project parcel; their module counts, angles and row layouts differ.
 *
 * Nothing is shown on a single-plane project.
 */
import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useIsGreek } from "../../context/LanguageContext";
import {
  displayName,
  orientationsFromConfig,
  orientationSummary,
} from "../config/orientations";

export default function LayoutOrientationPicker({ cfg, previewSystemDesign, activeId, onSelect }) {
  const isGreek = useIsGreek();
  const tr = (el, en) => (isGreek ? el : en);
  const orientations = orientationsFromConfig(cfg);
  if (orientations.length < 2) return null;
  const summaries = orientationSummary(previewSystemDesign ? { ...cfg, systemDesign: previewSystemDesign } : cfg);
  const active = orientations.find((o) => o.id === activeId) || orientations[0];
  const activeIndex = orientations.indexOf(active);
  const onRoof = String(cfg?.panelOrientation?.installationType || "").startsWith("roof");
  // On a roof the faces are picked in the scene and on the plan (a click on a
  // face makes it the one being edited), so this only says which one that is.
  if (onRoof) return (
    <Box sx={{ mb: 1.5 }} data-testid="roof-scene-selector">
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {tr("Κοινή σκηνή στέγης", "Shared roof scene")}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25 }} data-testid="roof-face-editing">
        {tr("Επεξεργάζεστε την πλευρά", "Editing face")}{" "}
        <Box component="span" sx={{ fontWeight: 700 }}>
          {`${activeIndex + 1} ${tr("από", "of")} ${orientations.length} · ${displayName(active, isGreek)}`}
        </Box>
        {` · ${summaries[activeIndex]?.modules || 0} ${tr("πάνελ", "modules")}`}
      </Typography>
      <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
        {tr("Κλικ σε άλλη πλευρά στη σκηνή ή στην κάτοψη την κάνει αυτή που επεξεργάζεστε· τα πεδία της φόρμας την ακολουθούν. Η αποθήκευση αποθηκεύει όλες τις πλευρές.",
          "Click another face in the scene or on the plan to edit it; the form fields follow it. Saving saves every face.")}
        {!summaries[activeIndex]?.modules && ` ${tr("Δεν έχουν συνδεθεί πάνελ σε αυτή την πλευρά. Η κατανομή ορίζεται στα MPPT του ηλεκτρολογικού σχεδιασμού.", "No modules are assigned to this surface. Assign them through the electrical design MPPTs.")}`}
      </Typography>
    </Box>
  );
  const hasLayout = (o, i) => (i === 0
    ? Boolean(cfg?.pv_design?.spacingInfo || cfg?.pv_design?.spacing)
    : Boolean(o.spacingInfo));

  return (
    <Box sx={{ mb: 1.5 }} data-testid="layout-orientation-picker">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {tr("Διάταξη του προσανατολισμού", "Layout of orientation")}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={active.id}
          onChange={(e, value) => value && onSelect(value)}
        >
          {orientations.map((o, i) => (
            <ToggleButton key={o.id} value={o.id} sx={{ textTransform: "none", px: 1.25, gap: 0.5 }}>
              {`${i + 1} · ${displayName(o, isGreek)}`}
              {!hasLayout(o, i) ? (
                <WarningAmberIcon
                  fontSize="inherit"
                  color="warning"
                  titleAccess={tr("Χωρίς αποθηκευμένη διάταξη", "No layout saved")}
                />
              ) : null}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 0.5 }}>
        {tr(
          "Όλοι οι προσανατολισμοί της μονάδας χρησιμοποιούν το κοινό γήπεδο που ορίζεται στη θέση εγκατάστασης. Οι σειρές και οι αποστάσεις ρυθμίζονται για τα πάνελ κάθε προσανατολισμού.",
          "All orientations of this unit use the common parcel defined at the installation location. Rows and spacing are configured for each orientation's modules."
        )}
        {" "}{tr(
          `${summaries[activeIndex]?.modules ? `${summaries[activeIndex].modules.toLocaleString("en-GB")} πλαίσια` : "Κανένα πλαίσιο"} σε αυτόν τον προσανατολισμό.`,
          `${summaries[activeIndex]?.modules ? `${summaries[activeIndex].modules.toLocaleString("en-GB")} modules` : "No modules"} on this orientation.`
        )}
      </Typography>
    </Box>
  );
}
